import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuHeartPulse, LuLoaderCircle, LuSave, LuShieldCheck, LuShieldPlus, LuStethoscope, LuUserPen } from 'react-icons/lu';
import { db } from '../../../config/firebaseConfig';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import SearchSelect from '../../../components/form/SearchSelect';
import { Field, TextInput } from '../../../components/form/FormControls';
import useInsuranceText from './insuranceText';
import { INSURANCE_TYPES } from './insuranceUtils';

const FORM_ID = 'insurance-form';
const TYPE_ICONS = { accidentInsurance: LuShieldCheck, complementaryHealth: LuHeartPulse, malpractice: LuStethoscope };

const emptyForm = () => ({
  employeeId: '',
  hasBES: false,
  accidentInsurance: { startDate: '', endDate: '', cost: '' },
  complementaryHealth: { startDate: '', endDate: '', cost: '' },
  malpractice: { startDate: '', endDate: '', cost: '' },
  cost: '',
});

const formFrom = (insurance) => (insurance ? {
  employeeId: insurance.employeeId,
  hasBES: Boolean(insurance.hasBES),
  ...Object.fromEntries(INSURANCE_TYPES.map((type) => [type, {
    startDate: insurance[type]?.startDate || '',
    endDate: insurance[type]?.endDate || '',
    cost: insurance[type]?.cost || '',
  }])),
  cost: insurance.cost || '',
} : emptyForm());

/**
 * Add or edit an insurance record (one form for both, replacing the old two
 * windows). The total is the sum of the per-policy costs; a record with no
 * per-policy cost (older entries) keeps a total that can be typed in, so
 * editing never drops a stored amount. Remounted on every open (key).
 */
const InsuranceFormModal = ({ isOpen, insurance, employees, onClose, onSaved }) => {
  const { label, language, formatMoney } = useInsuranceText();
  const isEditing = Boolean(insurance);
  const [formData, setFormData] = useState(() => formFrom(insurance));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Kişi bazlı maliyetlerden biri girildiyse toplam otomatik hesaplanır
  const hasTypeCosts = INSURANCE_TYPES.some((type) => String(formData[type].cost || '') !== '');
  const autoTotal = INSURANCE_TYPES.reduce((sum, type) => sum + (parseFloat(formData[type].cost) || 0), 0);
  const totalCost = hasTypeCosts ? (autoTotal > 0 ? autoTotal.toFixed(2) : '') : formData.cost;

  // Yalnızca aktif çalışanlar, alfabetik sıralı (eskisi gibi)
  const employeeOptions = useMemo(() => employees
    .filter((employee) => employee.Active === 1)
    .map((employee) => ({ value: employee.id, label: `${employee.eName || ''} ${employee.eSurname || ''}`.trim() }))
    .sort((a, b) => a.label.toLocaleLowerCase('tr').localeCompare(b.label.toLocaleLowerCase('tr'), 'tr')), [employees]);

  const setField = (field, value) => {
    if (errors[field]) setErrors((current) => ({ ...current, [field]: null }));
    setFormData((prev) => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return { ...prev, [parent]: { ...prev[parent], [child]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!isEditing && !formData.employeeId) newErrors.employeeId = label('form.errors.employee');
    if (totalCost && Number.isNaN(Number(totalCost))) newErrors.cost = label('form.errors.cost');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      toast.error(label('toast.formErrors'));
      return;
    }
    setSaving(true);
    try {
      const data = { ...formData, cost: totalCost };
      if (isEditing) {
        await updateDoc(doc(db, 'Insurances', insurance.id), { ...data, updatedAt: new Date() });
        toast.success(label('toast.updated'));
      } else {
        const selectedEmployeeData = employees.find((emp) => emp.id === formData.employeeId);
        await addDoc(collection(db, 'Insurances'), {
          ...data,
          employeeId: formData.employeeId,
          employeeName: `${selectedEmployeeData?.eName} ${selectedEmployeeData?.eSurname}`,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        toast.success(label('toast.added'));
      }
      onSaved();
      onClose();
    } catch (error) {
      console.error('Error adding/updating insurance:', error);
      toast.error(label('toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="form"
      dismissible={!saving}
      title={isEditing ? label('form.editTitle') : label('form.newTitle')}
      subtitle={isEditing ? insurance.employeeName : label('form.subtitle')}
      closeLabel={label('form.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" size="xs">
          {isEditing ? <LuUserPen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuShieldPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('form.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={saving}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('form.saving') : isEditing ? label('form.update') : label('form.save')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} className="tw-space-y-5">
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-[minmax(0,1fr)_auto]">
          {!isEditing ? (
            <Field label={label('form.employee')} htmlFor="insurance-employee" required error={errors.employeeId || undefined} hint={label('form.employeeHint')}>
              <SearchSelect
                inputId="insurance-employee"
                options={employeeOptions}
                value={formData.employeeId}
                onChange={(value) => setField('employeeId', value)}
                placeholder={label('form.employeePlaceholder')}
                noOptionsText={label('form.noEmployees')}
                isClearable
                error={errors.employeeId || undefined}
              />
            </Field>
          ) : <span aria-hidden="true" />}
          <Field label={label('form.bes')}>
            <SegmentedControl
              size="sm"
              layoutId="insurance-form-bes"
              label={label('form.bes')}
              value={formData.hasBES ? 'true' : 'false'}
              onChange={(value) => setField('hasBES', value === 'true')}
              options={[{ value: 'false', label: label('form.no') }, { value: 'true', label: label('form.yes') }]}
            />
          </Field>
        </div>

        {INSURANCE_TYPES.map((type) => {
          const Icon = TYPE_ICONS[type];
          return (
            <fieldset key={type} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/60 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
              <legend className="tw-flex tw-items-center tw-gap-2 tw-px-1.5 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-rose-500" />
                {label(`types.${type}`)}
              </legend>
              <div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
                <Field label={label('form.start')} htmlFor={`${type}-start`}>
                  <TextInput id={`${type}-start`} type="date" value={formData[type].startDate} onChange={(event) => setField(`${type}.startDate`, event.target.value)} className="dark:[color-scheme:dark]" />
                </Field>
                <Field label={label('form.end')} htmlFor={`${type}-end`}>
                  <TextInput id={`${type}-end`} type="date" value={formData[type].endDate} onChange={(event) => setField(`${type}.endDate`, event.target.value)} className="dark:[color-scheme:dark]" />
                </Field>
                <Field label={label('form.cost')} htmlFor={`${type}-cost`}>
                  <TextInput id={`${type}-cost`} type="number" step="0.01" inputMode="decimal" value={formData[type].cost} onChange={(event) => setField(`${type}.cost`, event.target.value)} placeholder="0.00" />
                </Field>
              </div>
            </fieldset>
          );
        })}

        <Field
          label={label('form.total')}
          htmlFor="insurance-total"
          error={errors.cost || undefined}
          hint={hasTypeCosts ? label('form.totalAuto') : label('form.totalManual')}
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            <TextInput
              id="insurance-total"
              value={totalCost}
              readOnly={hasTypeCosts}
              onChange={(event) => setField('cost', event.target.value)}
              inputMode="decimal"
              placeholder="0.00"
              error={errors.cost || undefined}
              className={`tw-max-w-[12rem] tw-font-semibold tw-tabular-nums ${hasTypeCosts ? 'tw-cursor-not-allowed tw-opacity-80' : ''}`}
            />
            {totalCost && !Number.isNaN(Number(totalCost)) && <span className="tw-text-sm tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{formatMoney(totalCost)}</span>}
          </div>
        </Field>
      </form>
    </Modal>
  );
};

InsuranceFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  insurance: PropTypes.object,
  employees: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default InsuranceFormModal;
